<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteElement"
      ></delete-modal>
    </transition>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmStockUpdate"
        :message="'¿Estás seguro que deseas actualizar el inventario?'"
        @close="confirmStockUpdate = false"
        @confirm="updateStock"
      ></confirmation-modal>
    </transition>

    <div v-if="!loading">
      <div v-if="product" class="flex flex-col">
        <div class="flex items-center space-x-2">
          <router-link :to="{ name: 'productsIndex' }" class="system-button">
            <arrow-icon class="w-4 rotate-90"></arrow-icon>
          </router-link>
        </div>

        <div class="grid grid-cols-1 gap-2 lg:grid-cols-3 mt-2">
          <div class="col-span-1">
            <div class="w-full aspect-square bg-white rounded-lg shadow-lg">
              <img
                class="rounded-lg object-contain object-center w-full h-full"
                :src="displayImage"
              />
            </div>

            <input
              class="hidden"
              type="file"
              name="image"
              :id="'profileImage' + product.id"
              ref="image"
              accept="image/*"
              @change="getImage"
            />

            <div v-if="!savingImage">
              <label
                v-if="!newImage"
                :for="'profileImage' + product.id"
                class="secondary-button font-normal mt-2"
              >
                + {{ product.image_url ? "Cambiar Imagen" : "Agregar Imagen" }}
              </label>
            </div>

            <div v-if="newImage" class="flex space-x-2 mt-2">
              <button
                class="delete-button"
                @click.prevent="
                  imagePreview = null;
                  newImage = null;
                "
              >
                Remover
              </button>
              <button class="secondary-button" @click.prevent="saveImage()">
                Guardar
              </button>
            </div>
          </div>

          <div class="card lg:col-span-2 h-fit">
            <h2>{{ product.name }}</h2>

            <p class="text-content-400">{{ product.sku }}</p>

            <div class="flex flex-wrap space-x-2">
              <p
                v-for="category in product.product_categories"
                :key="category.id"
                class="text-primary-500"
              >
                {{ category.name }}
              </p>
            </div>

            <p class="font-medium text-success-500">
              {{ integerToCurrency(product.unit_price) }}
            </p>

            <p>{{ product.description }}</p>

            <p class="font-medium">{{ product.stock }} en inventario</p>

            <div v-if="showStockForm">
              <input
                id="stock"
                type="number"
                step="any"
                v-model="stock"
                class="w-fit"
              />
              <validation-errors
                :errors="errorFor('stock')"
              ></validation-errors>

              <button
                class="cancel-button mt-2"
                @click.prevent="showStockForm = false"
              >
                Cancelar
              </button>

              <primary-button
                v-if="stock !== null"
                class="mx-0 mt-2"
                :loading="loadingStockUpdate"
                type="submit"
                @click.prevent="confirmStockUpdate = true"
                >Guardar</primary-button
              >
            </div>
            <div v-else>
              <button
                v-if="!showStockForm && hasPermission('edit_products')"
                class="secondary-button block mt-1"
                @click.prevent="showStockForm = true"
              >
                Actualizar Inventario
              </button>
            </div>

            <div class="flex space-x-4 items-center mt-4">
              <router-link
                v-if="hasPermission('edit_products')"
                :to="{ name: 'productsEditForm', params: { id: product.id } }"
                ><edit-button
              /></router-link>

              <delete-button
                v-if="hasPermission('delete_products')"
                class="delete-icon-button"
                @click.prevent="openDeleteModal()"
              />
            </div>
          </div>
        </div>

        <!-- Multi-image gallery -->
        <div class="card mt-4">
          <div class="flex items-center justify-between">
            <h3 class="font-medium">Galería de imágenes</h3>
            <label
              v-if="hasPermission('edit_products')"
              :for="'galleryImage' + product.id"
              class="secondary-button cursor-pointer"
            >
              + Agregar imagen
            </label>
            <input
              class="hidden"
              type="file"
              :id="'galleryImage' + product.id"
              accept="image/*"
              @change="onGalleryImageChange"
            />
          </div>

          <p v-if="!sortedImages.length" class="text-content-300 text-sm mt-2">
            No hay imágenes adicionales.
          </p>

          <div
            v-if="sortedImages.length"
            class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-3"
          >
            <div
              v-for="(img, index) in sortedImages"
              :key="img.id"
              class="border border-content-100 rounded-lg p-2 flex flex-col bg-white"
            >
              <img
                :src="img.url"
                :alt="img.alt_text || ''"
                class="w-full aspect-square object-contain bg-white rounded"
              />
              <input
                type="text"
                v-model="img.alt_text"
                class="mt-2 text-sm"
                placeholder="Texto alternativo"
                @blur="saveAltText(img)"
              />
              <div class="flex justify-between mt-2">
                <div class="flex space-x-1">
                  <button
                    type="button"
                    class="system-button"
                    :disabled="index === 0"
                    @click="moveImage(index, -1)"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    class="system-button"
                    :disabled="index === sortedImages.length - 1"
                    @click="moveImage(index, 1)"
                  >
                    ↓
                  </button>
                </div>
                <button
                  type="button"
                  class="delete-button text-xs"
                  @click="deleteImage(img.id)"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Variants -->
        <div class="card mt-4">
          <div class="flex items-center justify-between">
            <h3 class="font-medium">Variantes</h3>
            <button
              v-if="hasPermission('edit_products')"
              type="button"
              class="primary-button mx-0"
              @click="openVariantModal()"
            >
              + Crear Variante
            </button>
          </div>

          <table v-if="variants.length" class="table-auto text-sm mt-2 bg-white w-full">
            <thead class="text-left">
              <tr class="bg-primary-100">
                <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Nombre</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">SKU</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Stock</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Peso (g)</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Precio override</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Activa</th>
                <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="variant in variants" :key="variant.id" class="table-row">
                <td class="table-cell">{{ variant.name }}</td>
                <td class="table-cell font-mono">{{ variant.sku }}</td>
                <td class="table-cell">{{ variant.stock }}</td>
                <td class="table-cell">{{ variant.weight_grams ?? "—" }}</td>
                <td class="table-cell">
                  {{ variant.price_override !== null && variant.price_override !== undefined
                    ? integerToCurrency(variant.price_override)
                    : "—" }}
                </td>
                <td class="table-cell">{{ variant.is_active ? "Sí" : "No" }}</td>
                <td class="table-cell">
                  <div class="flex space-x-1">
                    <button
                      v-if="hasPermission('edit_products')"
                      type="button"
                      class="secondary-button text-xs"
                      @click="openVariantModal(variant)"
                    >
                      Editar
                    </button>
                    <button
                      v-if="hasPermission('edit_products')"
                      type="button"
                      class="delete-button text-xs"
                      @click="deleteVariant(variant)"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="text-content-300 text-sm mt-2">
            Este producto no tiene variantes.
          </p>
        </div>

        <!-- Variant modal -->
        <transition name="modal">
          <div
            v-if="showVariantModal"
            class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
            @click.self="closeVariantModal"
          >
            <div class="bg-white rounded-lg p-6 w-full max-w-lg">
              <h3 class="font-medium">
                {{ editingVariant ? "Editar variante" : "Nueva variante" }}
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div class="flex flex-col">
                  <label>Nombre</label>
                  <input type="text" v-model="variantForm.name" />
                </div>
                <div class="flex flex-col">
                  <label>SKU</label>
                  <input type="text" v-model="variantForm.sku" class="font-mono" />
                </div>
                <div class="flex flex-col">
                  <label>Stock</label>
                  <input type="number" min="0" v-model="variantForm.stock" />
                </div>
                <div class="flex flex-col">
                  <label>Peso (g)</label>
                  <input
                    type="number"
                    min="0"
                    v-model="variantForm.weight_grams"
                  />
                </div>
                <div class="flex flex-col">
                  <label>Precio override</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    v-model="variantForm.price_override"
                    placeholder="Usa precio del producto"
                  />
                </div>
                <div class="flex items-center mt-6">
                  <input
                    id="variant_active"
                    type="checkbox"
                    class="h-5 w-5"
                    v-model="variantForm.is_active"
                  />
                  <label for="variant_active" class="ml-2">Activa</label>
                </div>
              </div>

              <div v-if="editingVariant" class="mt-4">
                <p class="font-medium text-sm">Imagen</p>
                <div v-if="editingVariant.images && editingVariant.images.length" class="flex gap-2 mt-2 flex-wrap">
                  <div
                    v-for="img in editingVariant.images"
                    :key="img.id"
                    class="relative"
                  >
                    <img
                      :src="img.url"
                      class="w-20 h-20 object-contain bg-white rounded border"
                    />
                    <button
                      type="button"
                      class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                      @click="deleteVariantImage(img.id)"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  class="mt-2"
                  @change="onVariantImageChange"
                />
              </div>

              <div class="flex justify-end space-x-2 mt-6">
                <button type="button" class="cancel-button" @click="closeVariantModal">
                  Cancelar
                </button>
                <primary-button
                  type="button"
                  class="mx-0"
                  :loading="savingVariant"
                  @click="saveVariant"
                >
                  {{ editingVariant ? "Guardar" : "Crear" }}
                </primary-button>
              </div>
            </div>
          </div>
        </transition>

        <!-- <div class="card mt-2">
          <p class="font-medium">#{{ product.id }}</p>

          <div class="div-line"></div>

          <div class="flex justify-between items-top">
            <div class="w-20 h-20 relative flex">
              <img
                ref="imagePreview"
                class="rounded-full object-contain object-center w-full h-full border border-primary-500"
                :class="[{ 'animate-pulse': savingImage }]"
                :src="imagePreview"
              />

              <input
                class="hidden"
                type="file"
                name="image"
                :id="'profileImage' + product.id"
                ref="image"
                accept="image/*"
                @change="getImage"
              />

              <div v-if="!savingImage">
                <label
                  v-if="!newImage"
                  :for="'profileImage' + product.id"
                  class="cursor-pointer smooth hover:bg-primary-600 w-10 h-10 flex items-center bg-primary-500 rounded-full border-4 border-white absolute -right-2 -bottom-2"
                >
                  <camera-icon class="w-4 stroke-current mx-auto text-white" />
                </label>
              </div>
            </div>
          <div v-if="newImage" class="flex space-x-2 mt-2">
            <button
              class="secondary-button"
              @click.prevent="saveImage()"
            >
              Guardar
            </button>

            <button
              class="delete-button"
              @click.prevent="
                getImagePreview();
                newImage = null;
              "
            >
              Remover
            </button>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">SKU</p>
            <p>{{ product.sku }}</p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Nombre</p>
            <p>{{ product.name }}</p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Descripción</p>
            <p>{{ product.description }}</p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Precio unitario</p>
            <p>{{ integerToCurrency(product.unit_price) }}</p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">En inventario</p>
            <p>{{ product.stock }}</p>
          </div>

          <p class="font-medium text-sm mt-2">Categorías</p>
          <p
            class="text-sm"
            v-for="category in product.product_categories"
            :key="category.id"
          >
            - {{ category.name }}
          </p>

          <router-link
            v-if="hasPermission('edit_products')"
            :to="{ name: 'productsEditForm', params: { id: product.id } }"
            class="secondary-button mx-0 mt-4"
            >Editar</router-link
          >

          <button
            v-if="hasPermission('delete_roles')"
            class="mt-12 delete-button"
            @click="openDeleteModal()"
          >
            Eliminar Rol
          </button>
        </div> -->
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import validationErrors from "@/mixins/validationErrors";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
import { is422 } from "@/utils/response";
export default {
  name: "Product Detail",

  components: {
    ConfirmationModal,
  },

  data() {
    return {
      product: null,
      loading: false,
      loadingDelete: false,
      savingImage: false,
      imagePreview: null,
      newImage: null,
      imageIndex: 0,
      defaultImage: require("@/assets/paw.png"),
      stock: null,
      showStockForm: false,
      confirmStockUpdate: false,
      variants: [],
      showVariantModal: false,
      editingVariant: null,
      variantForm: this.emptyVariantForm(),
      savingVariant: false,
      newVariantImage: null,
    };
  },

  computed: {
    selectedImage() {
      if (!this.product || this.product.images.length === 0) {
        return null;
      }

      return this.product.images[this.imageIndex];
    },

    sortedImages() {
      if (!this.product || !Array.isArray(this.product.images)) return [];
      return [...this.product.images].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0)
      );
    },

    displayImage() {
      if (this.imagePreview) {
        return this.imagePreview;
      }

      const first = this.sortedImages[0];
      if (first?.url) return first.url;

      if (!this.product || !this.product.image_url) {
        return this.defaultImage;
      }

      return this.product.image_url;
    },
  },

  mixins: [deleteMixin, validationErrors],

  created() {
    this.$store.commit("setSelectedTab", "store");

    this.loadProduct();
  },

  methods: {
    formatDatetime,

    integerToCurrency,

    emptyVariantForm() {
      return {
        name: "",
        sku: "",
        stock: 0,
        weight_grams: null,
        price_override: null,
        is_active: true,
        position: 0,
      };
    },

    async loadProduct() {
      this.loading = true;

      try {
        this.product = await api.products.getById(this.$route.params.id);
        this.$store.commit('setBreadcrumbLabel', {
          routeName: 'productsDetail',
          label: this.product.name,
        });
        await this.loadVariants();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "products" });
      }

      this.loading = false;
    },

    async loadVariants() {
      try {
        this.variants = await api.productVariants.listForProduct(
          this.$route.params.id
        );
      } catch (error) {
        this.variants = [];
      }
    },

    async onGalleryImageChange(e) {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("product_id", this.product.id);
      formData.append("image", file);

      try {
        await api.products.saveImage(formData);
        this.$store.dispatch("successSnack", "Imagen guardada exitosamente.");
        e.target.value = "";
        await this.loadProduct();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async saveAltText(image) {
      try {
        await api.products.updateImageAltText(
          this.product.id,
          image.id,
          image.alt_text || ""
        );
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async moveImage(index, delta) {
      const next = index + delta;
      if (next < 0 || next >= this.sortedImages.length) return;
      const ids = this.sortedImages.map((img) => img.id);
      [ids[index], ids[next]] = [ids[next], ids[index]];

      try {
        await api.products.reorderImages(this.product.id, ids);
        await this.loadProduct();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async deleteImage(imageId) {
      try {
        await api.products.removeImage(this.product.id, imageId);
        this.$store.dispatch("successSnack", "Imagen eliminada.");
        await this.loadProduct();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    openVariantModal(variant = null) {
      this.editingVariant = variant;
      if (variant) {
        this.variantForm = {
          name: variant.name,
          sku: variant.sku,
          stock: variant.stock ?? 0,
          weight_grams: variant.weight_grams ?? null,
          price_override: variant.price_override ?? null,
          is_active: !!variant.is_active,
          position: variant.position ?? 0,
        };
      } else {
        this.variantForm = this.emptyVariantForm();
      }
      this.showVariantModal = true;
    },

    closeVariantModal() {
      this.showVariantModal = false;
      this.editingVariant = null;
      this.variantForm = this.emptyVariantForm();
      this.newVariantImage = null;
    },

    async saveVariant() {
      this.savingVariant = true;

      try {
        if (this.editingVariant) {
          await api.productVariants.update(
            this.editingVariant.id,
            this.variantForm
          );
          this.$store.dispatch("successSnack", "Variante actualizada.");
        } else {
          const response = await api.productVariants.create(
            this.product.id,
            this.variantForm
          );
          this.$store.dispatch(
            "successSnack",
            response.message || "Variante creada."
          );
        }

        await this.loadVariants();
        this.closeVariantModal();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.savingVariant = false;
    },

    async deleteVariant(variant) {
      if (!confirm(`¿Eliminar la variante "${variant.name}"?`)) return;

      try {
        await api.productVariants.delete(variant.id);
        this.$store.dispatch("successSnack", "Variante eliminada.");
        await this.loadVariants();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async onVariantImageChange(e) {
      if (!this.editingVariant) return;
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("image", file);

      try {
        await api.productVariants.uploadImage(this.editingVariant.id, formData);
        this.$store.dispatch("successSnack", "Imagen subida.");
        e.target.value = "";
        await this.loadVariants();
        this.editingVariant = this.variants.find(
          (v) => v.id === this.editingVariant.id
        );
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async deleteVariantImage(imageId) {
      if (!this.editingVariant) return;

      try {
        await api.productVariants.removeImage(this.editingVariant.id, imageId);
        await this.loadVariants();
        this.editingVariant = this.variants.find(
          (v) => v.id === this.editingVariant.id
        );
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    getImage(e) {
      let image = e.target.files[0];
      if (image) {
        let url = URL.createObjectURL(image);
        let img = new Image();

        img.onload = () => {
          URL.revokeObjectURL(url);
          let reader = new FileReader();
          reader.onload = (e) => {
            this.newImage = image;
            this.imagePreview = e.target.result;
          };
          reader.readAsDataURL(image);
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          alert("Unable to load image");
        };

        img.src = url;
      }
    },

    async saveImage() {
      if (!this.newImage) {
        return;
      }

      const formData = new FormData();

      formData.append("product_id", this.product.id);
      formData.append("image", this.newImage);

      this.savingImage = true;

      try {
        const response = await api.products.saveImage(formData);
        this.$store.dispatch("successSnack", response.message);
        this.newImage = null;
        this.loadProduct();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.savingImage = false;
    },

    async updateStock() {
      this.errors = {};
      this.confirmStockUpdate = false;
      this.loadingStockUpdate = true;

      try {
        const response = await api.products.updateStock(this.product.id, {
          stock: this.stock,
        });
        this.$store.dispatch("successSnack", response.message);
        this.showStockForm = false;
        await this.loadProduct();
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingStockUpdate = false;
    },

    async deleteElement() {
      this.loadingDelete = true;

      try {
        const response = await api.products.delete(this.product.id);
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
        this.$router.push({ name: "productsIndex" });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingDelete = false;
    },
  },
};
</script>